import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Gift } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  GRANT_TIERS,
  daysLeft,
  formatDate,
  formatDateTime,
  formatGrantEnd,
  grantStatusColor,
  grantStatusLabel,
} from "@/lib/billingGrant";
import { tierBadgeColor, tierBadgeLabel } from "@/lib/tierBadge";
import {
  createBillingGrant,
  extendBillingGrant,
  getOrganizationBillingGrants,
  revokeBillingGrant,
  type BillingGrant,
  type BillingGrantMutation,
  type OrganizationBillingGrants,
} from "@/services/billingGrantsApi";

type DialogKind = "create" | "extend" | "revoke" | null;

const EFFECTS_DELAYED =
  "L'accès est déjà actif. Le tier et les quotas seront appliqués dans l'heure (le back n'a pas répondu).";

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${grantStatusColor(status)}`}>
      {grantStatusLabel(status)}
    </span>
  );
}

function TierBadge({ tier }: { tier: string }) {
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${tierBadgeColor(tier)}`}>
      {tierBadgeLabel(tier)}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

/**
 * Carte « Accès offert » de la fiche organisation : accès gratuit à 100 %,
 * N jours ou illimité, avec un tier et un plafond de sièges, sans objet Stripe.
 * Écrit par backoffice ; le back applique les effets (tier, quotas, suspension
 * à la fin) et accorde l'accès à partir de la table.
 */
export default function OrganizationBillingGrantSection({
  organizationId,
}: {
  organizationId: number;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState<OrganizationBillingGrants | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [busy, setBusy] = useState(false);

  // Formulaire de création
  const [tier, setTier] = useState<string>("business");
  const [seatLimit, setSeatLimit] = useState("");
  const [unlimited, setUnlimited] = useState(false);
  const [durationDays, setDurationDays] = useState("30");
  const [reason, setReason] = useState("");
  // Prolongation
  const [extendDays, setExtendDays] = useState("30");
  const [extendUnlimited, setExtendUnlimited] = useState(false);
  // Révocation
  const [revokeReason, setRevokeReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getOrganizationBillingGrants(organizationId, navigate));
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Chargement de l'accès offert impossible",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, navigate, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = data?.active ?? null;

  const openCreate = () => {
    setTier(active?.tier ?? data?.organization.tier ?? "business");
    setSeatLimit("");
    setUnlimited(false);
    setDurationDays("30");
    setReason("");
    setDialog("create");
  };

  const openExtend = () => {
    setExtendDays("30");
    setExtendUnlimited(false);
    setDialog("extend");
  };

  const openRevoke = () => {
    setRevokeReason("");
    setDialog("revoke");
  };

  const run = async (action: () => Promise<BillingGrantMutation>, success: string) => {
    setBusy(true);
    try {
      const result = await action();
      setDialog(null);
      toast({
        title: success,
        description: result.effects_applied ? undefined : EFFECTS_DELAYED,
      });
      await load();
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Action impossible",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const submitCreate = () =>
    run(
      () =>
        createBillingGrant(
          organizationId,
          {
            tier,
            seat_limit: seatLimit.trim() ? Number(seatLimit) : null,
            duration_days: unlimited ? null : Number(durationDays),
            unlimited,
            reason: reason.trim(),
          },
          navigate
        ),
      active ? "Accès offert remplacé" : "Accès offert créé"
    );

  const submitExtend = (grant: BillingGrant) =>
    run(
      () =>
        extendBillingGrant(
          organizationId,
          grant.id,
          {
            extend_days: extendUnlimited ? null : Number(extendDays),
            unlimited: extendUnlimited,
          },
          navigate
        ),
      "Accès offert prolongé"
    );

  const submitRevoke = (grant: BillingGrant) =>
    run(
      () => revokeBillingGrant(organizationId, grant.id, revokeReason.trim(), navigate),
      "Accès offert révoqué"
    );

  const createValid =
    reason.trim().length >= 3 &&
    (unlimited || Number(durationDays) >= 1) &&
    (!seatLimit.trim() || Number(seatLimit) >= 1);
  const extendValid = extendUnlimited || Number(extendDays) >= 1;
  const remaining = active ? daysLeft(active.ends_at) : null;

  return (
    <section className="border rounded p-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Gift size={18} /> Accès offert
          {active ? (
            <StatusBadge status="active" />
          ) : (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              Aucun
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={openCreate} disabled={loading || busy}>
            {active ? "Remplacer" : "Offrir l'accès"}
          </Button>
          {active && (
            <>
              <Button size="sm" variant="outline" onClick={openExtend} disabled={busy}>
                Prolonger
              </Button>
              <Button size="sm" variant="destructive" onClick={openRevoke} disabled={busy}>
                Révoquer
              </Button>
            </>
          )}
        </div>
      </div>

      {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

      {data?.organization.has_stripe_subscription && (
        <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
          <AlertTriangle size={16} className="shrink-0" />
          Cette organisation a aussi un abonnement Stripe : il continue d'être facturé
          pendant l'accès offert.
        </div>
      )}

      {!loading && data && !active && (
        <p className="text-sm text-gray-500">
          Aucun accès offert en cours. L'organisation dépend de son abonnement Stripe.
        </p>
      )}

      {active && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Field label="Tier">
            <TierBadge tier={active.tier} />
          </Field>
          <Field label="Sièges">
            <span data-testid="grant-seats">
              {active.seat_limit ?? "plafond du tier"}
            </span>
          </Field>
          <Field label="Fin">
            <span data-testid="grant-end">
              {formatGrantEnd(active.ends_at)}
              {remaining !== null && ` (dans ${remaining} j)`}
            </span>
          </Field>
          <Field label="Accordé">
            {formatDate(active.created_at)} par {active.granted_by}
          </Field>
          <div className="col-span-2 md:col-span-4">
            <Field label="Motif">
              <span data-testid="grant-reason">{active.reason}</span>
            </Field>
          </div>
          {active.effects_pending && (
            <p className="col-span-2 md:col-span-4 text-xs text-amber-700">
              Tier et quotas en cours d'application (au plus tard dans l'heure).
            </p>
          )}
        </div>
      )}

      {data && data.history.length > 0 && (
        <div className="overflow-auto">
          <h3 className="text-sm font-semibold mb-2">Historique</h3>
          <table className="min-w-full text-sm" data-testid="grant-history">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">Tier</th>
                <th className="px-3 py-2 text-left">Sièges</th>
                <th className="px-3 py-2 text-left">Début</th>
                <th className="px-3 py-2 text-left">Fin</th>
                <th className="px-3 py-2 text-left">Motif</th>
                <th className="px-3 py-2 text-left">Accordé par</th>
                <th className="px-3 py-2 text-left">Révocation</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((g) => (
                <tr key={g.id} className="border-t align-top">
                  <td className="px-3 py-2">
                    <StatusBadge status={g.status} />
                    {g.suspended_org && (
                      <span className="ml-1 text-xs text-red-700">org suspendue</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <TierBadge tier={g.tier} />
                  </td>
                  <td className="px-3 py-2">{g.seat_limit ?? "-"}</td>
                  <td className="px-3 py-2">{formatDateTime(g.starts_at)}</td>
                  <td className="px-3 py-2">{g.ends_at ? formatDateTime(g.ends_at) : "Illimité"}</td>
                  <td className="px-3 py-2 max-w-xs break-words">{g.reason}</td>
                  <td className="px-3 py-2">{g.granted_by}</td>
                  <td className="px-3 py-2 max-w-xs break-words">
                    {g.revoked_at ? (
                      <>
                        {formatDateTime(g.revoked_at)} — {g.revoked_by}
                        {g.revoke_reason && (
                          <div className="text-xs text-gray-500">{g.revoke_reason}</div>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Création / remplacement */}
      <Dialog open={dialog === "create"} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{active ? "Remplacer l'accès offert" : "Offrir l'accès"}</DialogTitle>
            <DialogDescription>
              Accès gratuit à 100 %, sans objet Stripe.
              {active && " L'accès en cours sera clos et remplacé par celui-ci."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="grant-tier">Tier</Label>
              <select
                id="grant-tier"
                className="border rounded px-2 py-1 text-sm w-full"
                value={tier}
                onChange={(e) => setTier(e.target.value)}
              >
                {GRANT_TIERS.map((t) => (
                  <option key={t} value={t}>
                    {tierBadgeLabel(t)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="grant-seats-input">Plafond de sièges (vide = celui du tier)</Label>
              <Input
                id="grant-seats-input"
                type="number"
                min={1}
                value={seatLimit}
                onChange={(e) => setSeatLimit(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="grant-unlimited"
                type="checkbox"
                checked={unlimited}
                onChange={(e) => setUnlimited(e.target.checked)}
              />
              <Label htmlFor="grant-unlimited">Illimité (sans date de fin)</Label>
            </div>
            {!unlimited && (
              <div className="space-y-1">
                <Label htmlFor="grant-duration">Durée (jours)</Label>
                <Input
                  id="grant-duration"
                  type="number"
                  min={1}
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="grant-reason-input">Motif (obligatoire)</Label>
              <Textarea
                id="grant-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. client pilote, compte interne, geste commercial…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} disabled={busy}>
              Annuler
            </Button>
            <Button onClick={submitCreate} disabled={busy || !createValid}>
              Offrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prolongation */}
      <Dialog open={dialog === "extend"} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prolonger l'accès offert</DialogTitle>
            <DialogDescription>
              {active ? `Fin actuelle : ${formatGrantEnd(active.ends_at)}.` : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                id="extend-unlimited"
                type="checkbox"
                checked={extendUnlimited}
                onChange={(e) => setExtendUnlimited(e.target.checked)}
              />
              <Label htmlFor="extend-unlimited">Rendre illimité</Label>
            </div>
            {!extendUnlimited && (
              <div className="space-y-1">
                <Label htmlFor="extend-days">Jours supplémentaires</Label>
                <Input
                  id="extend-days"
                  type="number"
                  min={1}
                  value={extendDays}
                  onChange={(e) => setExtendDays(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} disabled={busy}>
              Annuler
            </Button>
            <Button
              onClick={() => active && submitExtend(active)}
              disabled={busy || !extendValid || !active}
            >
              Prolonger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Révocation */}
      <Dialog open={dialog === "revoke"} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Révoquer l'accès offert</DialogTitle>
            <DialogDescription>
              L'accès prend fin immédiatement. Sans abonnement propre, l'organisation est
              suspendue et ses invitations et messages en attente sont annulés.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="revoke-reason">Motif (obligatoire)</Label>
            <Textarea
              id="revoke-reason"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} disabled={busy}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => active && submitRevoke(active)}
              disabled={busy || revokeReason.trim().length < 3 || !active}
            >
              Révoquer l'accès
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
